import React, { useEffect, useMemo, useState } from "react";
import {
    Grid,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip,
    Checkbox,
    FormControl
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/lab/Autocomplete";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Card } from "reactstrap";
import { makeStyles } from "@mui/styles";
import CustomButton from "../../../src/components/CustomButton/CustomButton";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import SearchIcon from "@mui/icons-material/Search";
import Layout from "../../components/Wrappers/Layout";
import SOLogout from "../../components/Logout/SOLogout";
import regenerateToken from "../../components/RegenerateToken/regenerateToken";
import {
    getWalletTransactionList,
    resetGetWalletTransactionList
} from "../../redux/actions/getWalletTransactionListAction";
import Util from "../../validation/index";
import success from "../../assets/img/success.png";
import failed from "../../assets/img/failed.png";
import pending from "../../assets/img/pending.png";
import proccessing from "../../assets/img/proccessing.png";
import moment from "moment";
import { getUserNameContactSuggestionList } from "../../redux/actions/partnerGetUserNameContactSuggestionAction";
import getSymbolFromCurrency from "currency-symbol-map";
import { getCustomerIdAndNameList } from "../../redux/actions/getCustomerIdAndNameListAction";
import {
    partnerUpdateGatewayTransactions,
    resetUpdateGatewayTransaction
} from "../../redux/actions/partnerUpdateGatewayTransactionAction";

const useStyles = makeStyles({
    table: {
        minWidth: 650
    }
});

const Reconciliation = () => {
    const classes = useStyles();

    // ---- Pagination ----
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [pageNo, setPageNo] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [isSearchClick, setIsSearchClick] = useState(false); // forces pageNo=1 only on explicit Search click

    // ---- Search filters ----
    const [customerOrMobName, setCustomerOrMobName] = useState("");
    const [toCustName, setToCustName] = useState("");
    const [memberId, setMemberId] = useState("");
    const [transactionIds, setTransactionIds] = useState([]);
    const [transactionIdInput, setTransactionIdInput] = useState("");

    var date = new Date();
    const [selectedDate, setSelectedDate] = useState(
        new Date(date.getFullYear(), date.getMonth(), 1)
    );
    const [selectedToDate, setSelectedToDate] = useState(new Date());

    // ---- Transaction list ----
    const [getTransListRefundStatus, setGetTransListRefundStatus] = useState(false);
    const [getTransRefundListArray, setGetTransRefundListArray] = useState([]);

    // ---- Reconciliation selection / confirmation ----
    const [confirmReconciliationOpen, setConfirmReconciliationOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const getRowId = (row) => row.id ?? row.transactionId;

    // ---- Notification dialog (result of reconciliation action) ----
    const [notificationRefundOpen, setNotificationRefundOpen] = useState(false);

    const [user, setUser] = useState({
        getOrderId: ""
    });
    const { getOrderId } = user;

    const login = useSelector((state) => state.login);
    const regenerateTokenRes = useSelector(({ regenerateToken }) => regenerateToken);
    const getCustomerIdAndNameListRes = useSelector(
        ({ getCustomerIdAndNameList }) => getCustomerIdAndNameList
    );
    var authToken = regenerateTokenRes.data.AuthToken;

    const getWalletTransactionListRes = useSelector(
        ({ getWalletTransactionList }) => getWalletTransactionList
    );
    const getUsernameContactSuggestionRes = useSelector(
        ({ partnerGetUsernameContactSuggestionsLists }) => partnerGetUsernameContactSuggestionsLists
    );
    const UpdateGatewayTransactionRes = useSelector(
        ({ partnerUpdateGatewayTransactionStatus }) => partnerUpdateGatewayTransactionStatus
    );

    const dispatch = useDispatch();

    // ---- Pagination handlers ----
    const handleChangePageForward = (event, newPage) => {
        setIsSearchClick(false); // don't force pageNo back to 1 on arrow navigation
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        const currentFirstRowIndex = pageNo * rowsPerPage;
        const newPage = Math.floor(currentFirstRowIndex / newRowsPerPage);

        setIsSearchClick(false);
        setRowsPerPage(newRowsPerPage);
        setPageNo(newPage);
    };

    // ---- Filter field handlers ----
    const handleChange = ({ target: { name, value } }) => {
        if (name === "customerOrMobName") {
            setCustomerOrMobName(value);
        }
        if (name === "toCustName") {
            setToCustName(value);
        }
        if (name === "memberId") {
            setMemberId(value);
        }
    };

    const handleDateChange = (date) => setSelectedDate(date);
    const handleToDateChange = (date) => setSelectedToDate(date);

    const onInputChange = ({ target: { name, value } }, type) => {
        const obj = { ...user };

        if (type === "number") {
            obj[name] = Util.verifyMobile(value) ? value : obj[name];
        } else if (type === "alphabet") {
            obj[name] = Util.verifyAlphabet(value) ? value : obj[name];
        } else if (type === "both") {
            obj[name] = Util.checkWhiteSpace(value) ? value : obj[name];
        } else {
            obj[name] = Util.noWhiteSpace(value) ? value : obj[name];
        }
        setUser(obj);
    };

    // ---- Row selection ----
    const visibleRows = useMemo(
        () => [].concat(getTransRefundListArray || []),
        [getTransRefundListArray]
    );

    const selectAll =
        visibleRows.length > 0 && visibleRows.every((row) => selectedRows[getRowId(row)]);
    const hasSelectedRows = Object.values(selectedRows).some(Boolean);
    const selectedCount = Object.values(selectedRows).filter(Boolean).length;

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectedRows((prev) => {
            const next = { ...prev };
            visibleRows.forEach((row) => {
                const id = getRowId(row);
                if (checked) next[id] = true;
                else delete next[id];
            });
            return next;
        });
    };

    const handleSelectRow = (id) => (e) => {
        setSelectedRows((prev) => {
            const next = { ...prev };
            if (e.target.checked) next[id] = true;
            else delete next[id];
            return next;
        });
    };

    const clearSelection = () => setSelectedRows({});

    // ---- Reconciliation confirm / submit ----
    const openConfirmReconciliation = () => {
        if (!hasSelectedRows) return;
        setConfirmReconciliationOpen(true);
    };

    const handleConfirmReconciliation = () => {
        const request = {
            actionCustomerMasterRequestVO: {
                transactionIds: Object.keys(selectedRows).filter((id) => selectedRows[id]),
                initiatedBy: "partner",
                walletOwnerId:
                    login.data.subRole === "" ? login.data.partnerId : login.data.parentId
            }
        };

        dispatch(partnerUpdateGatewayTransactions(request, authToken));
        setConfirmReconciliationOpen(false);
    };

    const handleNoticationClose = () => {
        setNotificationRefundOpen(false);
        dispatch(resetUpdateGatewayTransaction());
    };

    // ---- Contact suggestions ----
    useEffect(() => {
        if (regenerateTokenRes.data.status === "success") {
            const getUserContactReq = {
                actionCustomerMasterRequestVO: {
                    customerMobileOrName: customerOrMobName,
                    walletOwnerId:
                        login.data.subRole === "" ? login.data.partnerId : login.data.parentId
                }
            };
            dispatch(getUserNameContactSuggestionList(getUserContactReq, authToken));
        }
    }, [regenerateTokenRes.data.status]);

    const filterOptions = (options, { inputValue }) => {
        if (inputValue.length < 3) return [];
        return options
            .filter(
                (option) =>
                    (option.mobile && option.mobile.includes(inputValue)) ||
                    (option.customerMobileOrName &&
                        option.customerMobileOrName.includes(inputValue))
            )
            .slice(0, 20);
    };

    const filterOptionsName = (options, { inputValue }) => {
        if (inputValue.length < 3) return [];
        return options
            .filter(
                (option) =>
                    option && option.customerMobileOrName && option.customerMobileOrName.includes(inputValue)
            )
            .slice(0, 20);
    };

    useEffect(() => {
        if (regenerateTokenRes.data.status === "success") {
            const getCustomerIdAndNameReq = {
                loginDetails: {
                    walletOwnerId:
                        login.data.subRole === "" ? login.data.partnerId : login.data.parentId
                },
                actionCustomerMasterRequestVO: {
                    customerNameOrId: ""
                }
            };
            dispatch(getCustomerIdAndNameList(getCustomerIdAndNameReq, authToken));
        }
    }, [regenerateTokenRes.data.status]);

    const filterOptionsNameId = (options, { inputValue }) => {
        if (inputValue.length < 3) return [];
        return options.filter((option) => option.includes(inputValue) || "").slice(0, 20);
    };

    // ---- Transaction list fetch ----
    const pageNumberOne = () => {
        return transactionIds.length > 0 || memberId || getOrderId || customerOrMobName || toCustName
            ? 1
            : 0;
    };

    const hasFilters = () => {
        return (
            transactionIds.length > 0 ||
            memberId !== "" ||
            getOrderId !== "" ||
            customerOrMobName !== "" ||
            toCustName !== ""
        );
    };

    const getWalletTransactionListReq = () => {
        setIsLoading(true);
        setGetTransListRefundStatus(true);
        setSearchLoading(true);

        const getTransactionTypeListReqIs = {
            walletTransactionMasterDetailsRequestVO: {
                walletOwnerId:
                    login.data.subRole === "" ? login.data.partnerId : login.data.parentId,
                currency: "",
                fromDate: moment(selectedDate).format("DD/MM/YYYY"),
                toDate: moment(selectedToDate).format("DD/MM/YYYY"),
                transactionIds: transactionIds,
                isFee: "N",
                walletCustomerId: memberId.split("-")[0] || "",
                transactionType: "",
                transactionSubType: "",
                toCustomerName: toCustName.trim(),
                toCustomerContact: customerOrMobName.split("-")[0].substring(1),
                status: "Processing", // backend filters to only these two statuses
                feeType: "",
                pageNo: pageNumberOne() && hasFilters() && isSearchClick ? 1 : pageNo + 1,
                records: rowsPerPage,
                orderDescription: getOrderId,
                studentId: "",
                paymentReference: "",
                timestamp: "",
                parentId: "",
                type: "reconcile"
            }
        };

        return dispatch(getWalletTransactionList(getTransactionTypeListReqIs, authToken));
    };

    const getTransactionListFunc = () => {
        if (!getTransListRefundStatus) return;

        if (getWalletTransactionListRes.data.status === "success") {
            setIsLoading(false);
            setSearchLoading(false);
            const fullList = [].concat(getWalletTransactionListRes.data.transactionList || []);
            const filteredList = fullList.filter(
                (row) => row.status === "Pending" || row.status === "Processing"
            );
            setGetTransRefundListArray(filteredList);
            return;
        }

        if (
            getWalletTransactionListRes.data.status === "failed" ||
            getWalletTransactionListRes.data.status === "Failed" ||
            getWalletTransactionListRes.data.status === "NOT FOUND" ||
            getWalletTransactionListRes.data.status === "Not Found"
        ) {
            setIsLoading(false);
            setSearchLoading(false);
            setGetTransRefundListArray(getWalletTransactionListRes.data);

            if (getWalletTransactionListRes.data.description === "session out") {
                SOLogout(dispatch, authToken, regenerateTokenRes.data.partnerId);
            }
            if (getWalletTransactionListRes.data.description === "Authorization failed") {
                regenerateToken(
                    dispatch,
                    authToken,
                    regenerateTokenRes.data.partnerId || localStorage.getItem("walletOwnerId")
                );
            }
        }
    };

    useEffect(() => {
        getWalletTransactionListReq();
    }, [pageNo, rowsPerPage]);

    useEffect(() => {
        if (getWalletTransactionListRes.data.status) {
            getTransactionListFunc();
        }
    }, [getWalletTransactionListRes]);

    useEffect(() => {
        if (getWalletTransactionListRes.data.status === "success" && transactionIds.length > 0) {
            setPageNo(0);
        }
    }, [getWalletTransactionListRes.data.status]);

    useEffect(() => {
        return () => {
            dispatch(resetGetWalletTransactionList());
        };
    }, []);

    useEffect(() => {
        if (UpdateGatewayTransactionRes.success || UpdateGatewayTransactionRes.failed) {
            setNotificationRefundOpen(true);
        }
    }, [UpdateGatewayTransactionRes]);

    return (
        <>
            <Layout>
                <Card>
                    <Grid container spacing={2} style={{ padding: "25px", marginTop: "2rem" }}>
                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="From Date"
                                        format="MM/dd/yyyy"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        disableFuture={true}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="To Date"
                                        format="MM/dd/yyyy"
                                        value={selectedToDate}
                                        onChange={handleToDateChange}
                                        disableFuture={true}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={transactionIds}
                                inputValue={transactionIdInput}
                                onInputChange={(_, newValue) => {
                                    if (newValue === "" || /^\d+$/.test(newValue)) {
                                        setTransactionIdInput(newValue);
                                    }
                                }}
                                onChange={(_, newValue) => setTransactionIds(newValue)}
                                onKeyDown={(e) => {
                                    const val = transactionIdInput.trim();
                                    if ((e.key === "Enter" || e.key === ",") && val) {
                                        e.preventDefault();
                                        if (!transactionIds.includes(val)) {
                                            setTransactionIds((prev) => [...prev, val]);
                                        }
                                        setTransactionIdInput("");
                                    }
                                    if (e.key === "Backspace" && !transactionIdInput && transactionIds.length) {
                                        setTransactionIds((prev) => prev.slice(0, -1));
                                    }
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            size="small"
                                            label={option}
                                            {...getTagProps({ index })}
                                            sx={{ height: "20px", fontSize: "11px" }}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Transaction Id(s)"
                                        variant="standard"
                                        margin="dense"
                                        style={{ marginTop: "3px" }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: {
                                                "&:before": { borderBottomColor: "gray" },
                                                "&:after": { borderBottomColor: "gray" }
                                            }
                                        }}
                                        InputLabelProps={{ sx: { color: "gray" } }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={[].concat(getCustomerIdAndNameListRes.data.currencyList || [""])}
                                getOptionLabel={(option) => option}
                                filterOptions={filterOptionsNameId}
                                value={memberId}
                                onChange={handleChange}
                                onInputChange={(event, newInputValue) => setMemberId(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Member ID"
                                        style={{ marginTop: "3px" }}
                                        type="tel"
                                        fullWidth
                                        {...params}
                                        name="memberId"
                                        variant="standard"
                                        sx={{
                                            "& .MuiInput-underline:before": { borderBottomColor: "gray" },
                                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                                borderBottomColor: "gray"
                                            },
                                            "& .MuiInput-underline:after": { borderBottomColor: "gray" }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="name"
                                label="Order Id"
                                fullWidth
                                name="getOrderId"
                                value={getOrderId}
                                variant="standard"
                                onChange={(e) => onInputChange(e, "both")}
                                style={{ marginTop: "3px" }}
                                InputProps={{
                                    sx: {
                                        "&:before": { borderBottomColor: "gray" },
                                        "&:after": { borderBottomColor: "gray" }
                                    }
                                }}
                                InputLabelProps={{ sx: { color: "gray" } }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={[].concat(getUsernameContactSuggestionRes.data.list || [""])}
                                getOptionLabel={(option) =>
                                    "+" + option.countryCode + " " + option.mobile + "-" + option.customerMobileOrName
                                }
                                filterOptions={filterOptions}
                                renderInput={(params) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Contact"
                                        style={{ marginTop: "3px" }}
                                        type="text"
                                        fullWidth
                                        {...params}
                                        value={customerOrMobName || ""}
                                        name="customerOrMobName"
                                        onBlur={handleChange}
                                        variant="standard"
                                        sx={{
                                            "& .MuiInput-underline:before": { borderBottomColor: "gray" },
                                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                                borderBottomColor: "gray"
                                            },
                                            "& .MuiInput-underline:after": { borderBottomColor: "gray" }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} lg={3}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={[].concat(getUsernameContactSuggestionRes.data.list || [""])}
                                getOptionLabel={(option) => option.customerMobileOrName}
                                filterOptions={filterOptionsName}
                                renderInput={(params) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="name"
                                        label="Beneficiary Name"
                                        style={{ marginTop: "3px" }}
                                        type="text"
                                        fullWidth
                                        {...params}
                                        value={toCustName || ""}
                                        name="toCustName"
                                        onBlur={handleChange}
                                        variant="standard"
                                        sx={{
                                            "& .MuiInput-underline:before": { borderBottomColor: "gray" },
                                            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                                borderBottomColor: "gray"
                                            },
                                            "& .MuiInput-underline:after": { borderBottomColor: "gray" }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Grid container style={{ padding: "10px", marginTop: "1.5rem", marginBottom: "15px" }}>
                        <Grid item md={5}>
                            <CustomButton
                                style={{ padding: "8px" }}
                                onClick={() => {
                                    setIsSearchClick(true);
                                    setPageNo(0);
                                    getWalletTransactionListReq();
                                }}
                                disabled={searchLoading}
                            >
                                {searchLoading ? (
                                    <>
                                        Searching...&nbsp;
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    </>
                                ) : (
                                    <>
                                        Search <SearchIcon />
                                    </>
                                )}
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Card>

                {isLoading ? (
                    <LoadingSpinner />
                ) : getTransRefundListArray.length === undefined || !getTransListRefundStatus ? (
                    <Card>
                        <Grid container style={{ padding: "10px" }}>
                            <Grid xs={12} sm={12} md={12} lg={12}>
                                <Typography variant="body1" style={{ fontWeight: "700" }} className="themeText">
                                    Report Table
                                </Typography>
                            </Grid>
                            <Grid xs={12} sm={12} md={12} lg={12}>
                                <Alert className="my-3 tableHeaderColor" variant="secondary" style={{ color: "white" }}>
                                    No Records Found!
                                </Alert>
                            </Grid>
                        </Grid>
                    </Card>
                ) : (
                    <div>
                        <Card>
                            <Grid container style={{ padding: "10px" }}>
                                <Grid xs={12} sm={12} md={12} lg={12}>
                                    <Typography variant="body1" style={{ fontWeight: "700" }} className="themeText">
                                        Reconciliation Details
                                    </Typography>
                                </Grid>
                                <TableContainer component={Paper} className="my-3">
                                    <Table className={classes.table}>
                                        <TableHead>
                                            <TableRow className="tableHeaderColor">
                                                <TableCell align="center" style={{ color: "white" }}>
                                                    <Checkbox
                                                        checked={selectAll}
                                                        onChange={handleSelectAll}
                                                        sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" style={{ fontWeight: "bold", color: "white" }}>
                                                    Transaction Date{" "}
                                                </TableCell>
                                                <TableCell align="center" style={{ fontWeight: "bold", color: "white" }}>
                                                    {" "}
                                                    Transaction ID
                                                </TableCell>
                                                <TableCell align="center" style={{ fontWeight: "bold", color: "white" }}>
                                                    {" "}
                                                    Order ID
                                                </TableCell>
                                                <TableCell align="center" style={{ fontWeight: "bold", color: "white" }}>
                                                    Captured Amount
                                                </TableCell>
                                                <TableCell align="center" style={{ fontWeight: "bold", color: "white" }}>
                                                    Status
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {visibleRows &&
                                                visibleRows.map((row, index) => (
                                                    <TableRow key={getRowId(row) ?? index}>
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                checked={!!selectedRows[getRowId(row)]}
                                                                onChange={handleSelectRow(getRowId(row))}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">{row.registeredTime}</TableCell>
                                                        <TableCell align="center">{row.id || row.transactionId}</TableCell>
                                                        <TableCell align="center">{row.orderDescription}</TableCell>
                                                        <TableCell align="center">
                                                            {getSymbolFromCurrency(row.currency) + " " + row.amount}
                                                        </TableCell>
                                                        <TableCell align="center">{row.status}</TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>

                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: "12px",
                                            padding: "10px 0"
                                        }}
                                    >
                                        {hasSelectedRows && (
                                            <CustomButton
                                                style={{ padding: "8px 16px", backgroundColor: "#aaa" }}
                                                onClick={clearSelection}
                                            >
                                                Clear Selection ({selectedCount})
                                            </CustomButton>
                                        )}
                                        <CustomButton
                                            style={{ padding: "8px 16px" }}
                                            onClick={openConfirmReconciliation}
                                            disabled={!hasSelectedRows}
                                        >
                                            Reverse Selected ({selectedCount})
                                        </CustomButton>
                                    </div>

                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25]}
                                        component="div"
                                        count={getWalletTransactionListRes.data.count}
                                        rowsPerPage={rowsPerPage}
                                        page={pageNo}
                                        onPageChange={handleChangePageForward}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                </TableContainer>
                            </Grid>
                        </Card>
                    </div>
                )}

                <Dialog
                    open={confirmReconciliationOpen}
                    onClose={() => setConfirmReconciliationOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Confirm Reconciliation</DialogTitle>

                    <DialogContent>
                        <Typography>Do you really want to reconcile the selected transaction(s)?</Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setConfirmReconciliationOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleConfirmReconciliation}>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            </Layout>

            {UpdateGatewayTransactionRes?.data?.status && (
                <Dialog open={notificationRefundOpen} onClose={handleNoticationClose}>
                    <DialogContent className="text-center" style={{ backgroundColor: "#00b9f51c" }}>
                        <img
                            src={
                                UpdateGatewayTransactionRes.data.status === "Not Found"
                                    ? failed
                                    : UpdateGatewayTransactionRes.data.status === "Pending"
                                    ? pending
                                    : UpdateGatewayTransactionRes.data.status === "proccessing"
                                    ? proccessing
                                    : success
                            }
                            alt=""
                            width="100px"
                            className="my-2"
                        />
                    </DialogContent>

                    <DialogContent>
                        <h4 style={{ margin: "0", textAlign: "center", fontSize: "15px" }}>
                            {UpdateGatewayTransactionRes.data.description}
                        </h4>
                    </DialogContent>

                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            style={{ margin: "10px 0", backgroundColor: "#00b9f5", color: "white" }}
                            variant="contained"
                            onClick={handleNoticationClose}
                        >
                            Ok
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default Reconciliation;